import type { Document, DocumentReviewStatus } from '@onefootprint/request-types/dashboard';

/** A composite representation of the document's upload status AND review status */
export enum DocumentStatus {
  UploadFailed = 'upload_failed',
  UploadIncomplete = 'upload_incomplete',
  UploadAbandoned = 'upload_abandoned',
  UploadedViaApi = 'uploaded_via_api',
  Uploaded = 'uploaded',
  PendingMachineReview = 'pending_machine_review',
  ReviewedByMachine = 'reviewed_by_machine',
  PendingHumanReview = 'pending_human_review',
  ReviewedByHuman = 'reviewed_by_human',
}

/** How loud each status reads, in the tones a Badge and a card's status share. */
export const DOCUMENT_STATUS_TONE: Record<DocumentStatus, 'neutral' | 'warning' | 'error'> = {
  [DocumentStatus.UploadedViaApi]: 'neutral',
  [DocumentStatus.Uploaded]: 'neutral',
  [DocumentStatus.ReviewedByMachine]: 'neutral',
  [DocumentStatus.ReviewedByHuman]: 'neutral',
  [DocumentStatus.UploadAbandoned]: 'neutral',
  [DocumentStatus.UploadIncomplete]: 'warning',
  [DocumentStatus.PendingMachineReview]: 'error',
  [DocumentStatus.PendingHumanReview]: 'error',
  [DocumentStatus.UploadFailed]: 'error',
};

export const getDocumentStatus = (document: Omit<Document, 'uploads'>) => {
  if (!document.status) {
    return DocumentStatus.UploadedViaApi;
  }
  if (document.status === 'failed') {
    return DocumentStatus.UploadFailed;
  }
  if (document.status === 'pending') {
    return DocumentStatus.UploadIncomplete;
  }
  if (document.status === 'abandoned') {
    return DocumentStatus.UploadAbandoned;
  }

  // If the upload is complete, show a status badge that depends on the review status
  if (!document.reviewStatus) {
    return DocumentStatus.UploadIncomplete;
  }
  const reviewStatusToDocumentStatus: Record<DocumentReviewStatus, DocumentStatus | null> = {
    not_needed: null,
    unreviewed: DocumentStatus.Uploaded,
    pending_human_review: DocumentStatus.PendingHumanReview,
    pending_machine_review: DocumentStatus.PendingMachineReview,
    reviewed_by_machine: DocumentStatus.ReviewedByMachine,
    reviewed_by_human: DocumentStatus.ReviewedByHuman,
  };
  return reviewStatusToDocumentStatus[document.reviewStatus];
};

export default getDocumentStatus;
